/**
 * Wire protocol between a client and the game server (docs/server-model.md).
 * The client sends intents, the server sends facts. Control messages are JSON
 * text; chunks are binary frames. Compression is the transport's job
 * (WebSocket permessage-deflate takes a chunk from 98 KB to ~2.5 KB).
 */

import { CHUNK_VOLUME } from '../world/chunk';
import type { DimensionId } from '../world/dimension';
import type { GameMode, Stack } from '../game/types';
import type { MobRow } from '../entities/persist';

export const PROTOCOL_VERSION = 2;

/** Player state bits carried by `move` and `players` (pose and animation only: nothing here grants anything). */
export const MF_SNEAK = 1, MF_SPRINT = 2, MF_SWIM = 4, MF_FLY = 8, MF_GROUND = 16;

export type ClientMsg =
  /** First message. `key` is a secret the client keeps; the player id is its hash. `view` = chunk radius wanted. */
  | { t: 'hello'; proto: number; name: string; key: string; view: number }
  /** Own position, 20 Hz. The server checks it and answers with `pos` when it refuses. */
  | { t: 'move'; x: number; y: number; z: number; yaw: number; pitch: number; f: number }
  /**
   * Breaking a block: `a` = 0 start, 1 abort, 2 finish (absent = finish), `face` = the face being hit
   * (index into blocks.ts FACE_DIRS: +X -X +Y -Y +Z -Z). The server times the dig from the start
   * (actions/blocks.ts); a finish is answered by `ack` with the same seq, a start only when refused.
   */
  | { t: 'dig'; seq: number; x: number; y: number; z: number; a?: 0 | 1 | 2; face?: number }
  /** Put block `id` in a cell (creative players only; survival places with `useOn`). Answered by `ack` with the same seq. */
  | { t: 'place'; seq: number; x: number; y: number; z: number; id: number }
  | { t: 'chat'; text: string }
  /** The text of the sign this player just placed or right-clicked (actions/utility.ts): four lines, cut and cleaned by the server. */
  | { t: 'sign'; x: number; y: number; z: number; lines: string[] }
  | ClientMsgV2
  | ClientMsgCombat
  | ClientMsgMobs;

/** [id, x, y, z, yaw, pitch, flags] */
export type PlayerRow = [string, number, number, number, number, number, number];

export type ServerMsg =
  | { t: 'welcome'; proto: number; id: string; seed: number; wgv: number; time: number; dim: DimensionId;
      x: number; y: number; z: number; yaw: number; pitch: number; players: { id: string; name: string }[];
      /** v2: own entity id (players share the id space of mobs and items), game mode, window 0 contents, selected slot, stats. */
      eid: number; mode: GameMode; inv: WireStack[]; sel: number; stats: StatsFields }
  | { t: 'refuse'; why: string }
  /** The chunk is no longer kept up to date for this client: drop it. */
  | { t: 'unload'; cx: number; cz: number }
  /** Blocks that changed in chunks this client holds, flat [x, y, z, id, ...]. */
  | { t: 'blocks'; e: number[] }
  /** Verdict on a dig/place: the cell's real block, whatever the client predicted. */
  | { t: 'ack'; seq: number; ok: boolean; x: number; y: number; z: number; id: number; why?: string }
  /** Everyone else in this client's dimension. */
  | { t: 'players'; p: PlayerRow[] }
  | { t: 'join'; id: string; name: string }
  | { t: 'leave'; id: string }
  /** Day fraction 0..1 (0.25 = noon side of sunrise, as render/sky.ts). */
  | { t: 'time'; time: number }
  /** The server moved this client (spawn, refused move, teleport). */
  | { t: 'pos'; x: number; y: number; z: number; dim: DimensionId }
  /** `to`: a whisper (/msg), sent to that player and its sender only. */
  | { t: 'chat'; from: string; text: string; to?: string }
  | ServerMsgV2
  | ServerMsgMobs;

// ---- protocol v2: players, inventories, entities (server core) ---------------
// Meaning of every message: docs/server-model-plan.md. Stacks are the `Stack`
// object as JSON, null for an empty slot.

/** More pose bits for `move` / BIN_ENTS player flags. */
export const MF_USING = 32, MF_BLOCKING = 64;

export type WireStack = Stack | null;

/** Window 0 is the player's own inventory: slots 0..35 main (0..8 hotbar), 36..39 armour (helmet..boots), 40 offhand. */
export const WIN_INVENTORY = 0;
export const INV_SLOTS = 41;
export const SLOT_ARMOR = 36, SLOT_OFFHAND = 40;

export type ClientMsgV2 =
  /** Selected hotbar slot 0..8. */
  | { t: 'held'; slot: number }
  /** Right-click on a block face (`face` as in `dig`); (hx, hy, hz) = hit point inside the cell 0..1, hand 0 main / 1 off. */
  | { t: 'useOn'; seq: number; x: number; y: number; z: number; face: number; hx: number; hy: number; hz: number; hand: number }
  /** Right-click in the air (start eating, drawing a bow, throw, cast). */
  | { t: 'use'; seq: number; hand: number }
  /** Stopped holding right-click. */
  | { t: 'release' }
  /** Left-click on entity `id`. */
  | { t: 'attack'; id: number }
  /** Right-click on entity `id`. */
  | { t: 'interact'; id: number; hand: number }
  /**
   * Window click. `slot` is the menu's slot index (game/menu.ts builders; -999 = outside the panel), `mode` is
   * game/menu.ts CLICK: 0 pickup, 1 quick move, 2 swap (btn = hotbar 0..8, 40 = offhand), 3 clone, 4 throw,
   * 5 drag (`slots`), 6 pickup-all, 7 menu button (`text` = anvil name), 8 creative set (`item`), 9 recipe fill (`grid`).
   * `seq` must grow; every click is answered with `clicked`.
   */
  | { t: 'click'; win: number; slot: number; btn: number; mode: number; seq: number; slots?: number[]; text?: string; item?: WireStack; grid?: number[] }
  | { t: 'close'; win: number }
  /** Q on the held stack: one item, or the whole stack with `all`. */
  | { t: 'drop'; all?: boolean }
  /** After death: come back at the spawn point. */
  | { t: 'respawn' };

// ---- combat and survival (actions/combat.ts and friends) -----------------------

/** Pose bit: gliding on an elytra (the server allows the speed and does not count the glide as a fall). */
export const MF_GLIDE = 128;

/** Intents of the combat module (sent with `srv.sendRaw`). */
export type ClientMsgCombat =
  /** Get out of bed. */
  | { t: 'wake' };

/**
 * `fx` kinds the combat module sends (d = payload):
 * 'hurt' {eid, kind, amount, x, z} · 'death' {eid} · 'totem' {eid} · 'crit' {eid} (target) · 'swing' {eid} ·
 * 'sweep' {eid} · 'shield' {eid} (a block) · 'shield_break' {eid} · 'bow' {eid, power} · 'crossbow' {eid} ·
 * 'throw' {eid, item} · 'eat' {eid, item} (munching) · 'burp' {eid} · 'drink' {eid} · 'splash' {color, linger} ·
 * 'pearl' · 'bobber' {k} (BobberFx) · 'break' {eid, item} (a tool broke) · 'sleep' {eid, text?} · 'wake' {eid} ·
 * 'msg' {text} (action-bar line for one player).
 * Thrown things are tracked entities of kind 'thrown': record [item id, potion dmg, x, y, z, vx, vy, vz, owner eid],
 * kind 'trident': [x, y, z, vx, vy, vz, stuck, enchanted, owner eid], kind 'bobber': [x, y, z, owner eid, state],
 * kind 'cloud' (lingering potion): [x, y, z, radius, color].
 * kind 'falling' (sand, gravel, anvil, dragon egg in the air; world/falling.ts): [x, y, z, block id], x/z the centre, y the bottom.
 */

/** What kind of thing an entity id is. Wave 2 adds kinds (boat, cart, thrown, ...) through the tracker. */
export type EntityKind = 'mob' | 'item' | 'xp' | 'arrow' | 'player' | (string & {});

/** kind 'mob': the mob's saved row (entities/persist.ts `serializeMob` / `deserializeMob`). */
export type MobRecord = MobRow;
/** kind 'item': [stack, x, y, z, vx, vy, vz] */
export type ItemRecord = [Stack, number, number, number, number, number, number];
/** kind 'xp': [value of one orb, orbs merged, x, y, z] */
export type XpRecord = [number, number, number, number, number];
/** kind 'arrow': [x, y, z, vx, vy, vz, stuck, trident, shooter entity id] */
export type ArrowRecord = [number, number, number, number, number, number, 0 | 1, 0 | 1, number];
/** kind 'player': [player id, name, x, y, z, yaw, pitch, held item id, armour ids helmet..boots, offhand item id, MF_* flags] */
export type PlayerRecord = [string, string, number, number, number, number, number, number, number[], number, number];

/** [entity id, kind, record] */
export type SpawnRow = [number, EntityKind, unknown];

export interface StatsFields {
  health: number; food: number; sat: number;
  /** Air ticks left, 0..300. */
  air: number;
  xpLevel: number; xpProgress: number; xpTotal: number;
  /** EffectSet.serialize(): [id, amplifier, ticks left, ambient] per effect. */
  fx: number[];
  mode: GameMode;
  /** Burning. */
  fire: boolean;
  /** Absorption health points. */
  abs: number;
  dead: boolean;
}

export type ServerMsgV2 =
  /** Entities that entered this client's tracking range. */
  | { t: 'spawn'; e: SpawnRow[] }
  /** [entity id, record]: the record changed (sheared, named, held item, stack count, ...). */
  | { t: 'meta'; e: [number, unknown][] }
  /** Entities that left range or no longer exist. */
  | { t: 'despawn'; ids: number[] }
  /** `n` of item entity `id` went to entity `by` (play the fly-to-player pickup; a `despawn` follows when it is used up). */
  | { t: 'collect'; id: number; by: number; n: number }
  /** Changed slots of a window: [slot, stack]. Window 0 = own inventory. */
  | { t: 'slots'; win: number; s: [number, WireStack][] }
  /** The stack on the cursor. */
  | { t: 'cursor'; s: WireStack }
  /** The server opened a container window (`kind` = 'chest', 'furnace', ...; slot layout is the kind's). */
  | { t: 'open'; win: number; kind: string; title?: string; size?: number; x?: number; y?: number; z?: number }
  | { t: 'closed'; win: number }
  /** Answer to `click` seq, sent after the `slots` / `cursor` it caused: the client's prediction is now settled. */
  | { t: 'clicked'; seq: number; ok: boolean }
  /** A window property (furnace progress, brewing time, enchant offers). */
  | { t: 'prop'; win: number; k: string; v: number }
  /** Own health, food, air, XP, effects, mode. Sent when something changed. */
  | ({ t: 'stats' } & StatsFields)
  /**
   * The server pushed this client, blocks per tick. Plain: add to its velocity
   * (explosion). `kb`: a knockback, applied as Player.knockback does - halve the
   * horizontal velocity and add (x, z); when on the ground, vy = min(0.4, vy / 2 + y).
   */
  | { t: 'vel'; x: number; y: number; z: number; kb?: boolean }
  /** A sound / particle event the client did not cause itself. `k` names it ('hurt', 'death', 'explosion', 'poof', 'pickup', ...). */
  | { t: 'fx'; k: string; x: number; y: number; z: number; d?: unknown }
  /** A player died. */
  | { t: 'death'; id: string; eid: number; name: string; cause: string }
  | { t: 'weather'; rain: number; thunder: number };

/** Per-tick movement and animation of the entities a client tracks (see encodeEnts). */
export const BIN_ENTS = 2;

/** Which groups an entity carries in a BIN_ENTS frame. */
export const EM_POS = 1, EM_ROT = 2, EM_LIMB = 4, EM_STATE = 8, EM_ALL = 15;
/** Entity flags, low byte of `flags` (the high byte is a player's MF_* pose bits). */
export const EF_FIRE = 1, EF_AGGRESSIVE = 2, EF_STUCK = 4, EF_BABY = 8, EF_RIDDEN = 16;

/** One entity's state as the tracker hands it to encodeEnts (everything set; the mask picks what is written). */
export interface EntMove {
  id: number;
  x: number; y: number; z: number;
  /** Radians. */
  yaw: number; headYaw: number; pitch: number;
  limbSwing: number;
  /** 0..1 */
  limbAmt: number; swell: number; attackAnim: number;
  /** Ticks, 0..255. */
  hurtTime: number; deathTime: number;
  flags: number;
}

/** What a client reads back: only the groups that were sent are present. */
export type EntDelta = { id: number } & Partial<Omit<EntMove, 'id'>>;

const ENTS_HEADER = 8; // tag u8, pad u8, count u16, tick u32
const TAU = Math.PI * 2;
const q8 = (v: number): number => Math.max(0, Math.min(255, Math.round(v * 255)));
/** Angle -> 0..255 (1.4 degree steps). */
export const angle8 = (rad: number): number => Math.round((rad / TAU) * 256) & 255;
/** Pitch (-pi/2..pi/2) -> -127..127. */
export const pitch8 = (rad: number): number => Math.max(-127, Math.min(127, Math.round((rad / (Math.PI / 2)) * 127)));

/**
 * A BIN_ENTS frame (little-endian): header, then per entity `id u32, mask u8`
 * and the groups in the mask: EM_POS x y z f32; EM_ROT yaw u8, headYaw u8,
 * pitch i8; EM_LIMB limbSwing f32, limbAmt u8; EM_STATE swell u8, attackAnim
 * u8, hurtTime u8, deathTime u8, flags u16. `masks[i]` belongs to `ents[i]`;
 * entities whose mask is 0 are left out.
 */
export function encodeEnts(tick: number, ents: readonly EntMove[], masks: ArrayLike<number>): Uint8Array {
  let size = ENTS_HEADER, count = 0;
  for (let i = 0; i < ents.length; i++) {
    const m = masks[i] & EM_ALL;
    if (!m) continue;
    count++;
    size += 5 + (m & EM_POS ? 12 : 0) + (m & EM_ROT ? 3 : 0) + (m & EM_LIMB ? 5 : 0) + (m & EM_STATE ? 6 : 0);
  }
  const out = new Uint8Array(size);
  const dv = new DataView(out.buffer);
  dv.setUint8(0, BIN_ENTS);
  dv.setUint16(2, count, true);
  dv.setUint32(4, tick >>> 0, true);
  let o = ENTS_HEADER;
  for (let i = 0; i < ents.length; i++) {
    const m = masks[i] & EM_ALL;
    if (!m) continue;
    const e = ents[i];
    dv.setUint32(o, e.id >>> 0, true); dv.setUint8(o + 4, m); o += 5;
    if (m & EM_POS) { dv.setFloat32(o, e.x, true); dv.setFloat32(o + 4, e.y, true); dv.setFloat32(o + 8, e.z, true); o += 12; }
    if (m & EM_ROT) { dv.setUint8(o, angle8(e.yaw)); dv.setUint8(o + 1, angle8(e.headYaw)); dv.setInt8(o + 2, pitch8(e.pitch)); o += 3; }
    if (m & EM_LIMB) { dv.setFloat32(o, e.limbSwing, true); dv.setUint8(o + 4, q8(e.limbAmt)); o += 5; }
    if (m & EM_STATE) {
      dv.setUint8(o, q8(e.swell)); dv.setUint8(o + 1, q8(e.attackAnim));
      dv.setUint8(o + 2, Math.max(0, Math.min(255, e.hurtTime | 0))); dv.setUint8(o + 3, Math.max(0, Math.min(255, e.deathTime | 0)));
      dv.setUint16(o + 4, e.flags & 0xffff, true); o += 6;
    }
  }
  return out;
}

/** Decode a BIN_ENTS frame, or null when it is not one / is cut short. Angles come back in radians (yaw 0..2pi). */
export function decodeEnts(frame: Uint8Array): { tick: number; ents: EntDelta[] } | null {
  if (frame.byteLength < ENTS_HEADER || frame[0] !== BIN_ENTS) return null;
  const dv = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
  const count = dv.getUint16(2, true);
  const ents: EntDelta[] = [];
  let o = ENTS_HEADER;
  for (let i = 0; i < count; i++) {
    if (o + 5 > frame.byteLength) return null;
    const e: EntDelta = { id: dv.getUint32(o, true) };
    const m = dv.getUint8(o + 4); o += 5;
    const need = (m & EM_POS ? 12 : 0) + (m & EM_ROT ? 3 : 0) + (m & EM_LIMB ? 5 : 0) + (m & EM_STATE ? 6 : 0);
    if (o + need > frame.byteLength) return null;
    if (m & EM_POS) { e.x = dv.getFloat32(o, true); e.y = dv.getFloat32(o + 4, true); e.z = dv.getFloat32(o + 8, true); o += 12; }
    if (m & EM_ROT) {
      e.yaw = (dv.getUint8(o) / 256) * TAU; e.headYaw = (dv.getUint8(o + 1) / 256) * TAU; e.pitch = (dv.getInt8(o + 2) / 127) * (Math.PI / 2);
      o += 3;
    }
    if (m & EM_LIMB) { e.limbSwing = dv.getFloat32(o, true); e.limbAmt = dv.getUint8(o + 4) / 255; o += 5; }
    if (m & EM_STATE) {
      e.swell = dv.getUint8(o) / 255; e.attackAnim = dv.getUint8(o + 1) / 255;
      e.hurtTime = dv.getUint8(o + 2); e.deathTime = dv.getUint8(o + 3); e.flags = dv.getUint16(o + 4, true); o += 6;
    }
    ents.push(e);
  }
  return { tick: dv.getUint32(4, true), ents };
}

// ---- chunk frames ------------------------------------------------------------

export const BIN_CHUNK = 1;
/** Chunk frame flag: the payload after the header is deflate-raw of the block ids. */
export const CF_DEFLATE = 1;
export const CHUNK_HEADER = 12; // tag u8, dim u8, flags u16, cx i32, cz i32
const DIM_IDS: readonly DimensionId[] = ['overworld', 'nether', 'end'];
export const dimIndex = (d: DimensionId): number => DIM_IDS.indexOf(d);

/**
 * One chunk as a binary frame (little-endian). `payload` is either the block
 * ids themselves (Uint16Array, flags 0) or their deflate-raw bytes
 * (Uint8Array, CF_DEFLATE): the server compresses a chunk once and sends the
 * same bytes to every client.
 */
export function encodeChunk(dim: DimensionId, cx: number, cz: number, payload: Uint16Array | Uint8Array): Uint8Array {
  const raw = payload instanceof Uint16Array;
  const bytes = raw ? new Uint8Array(payload.buffer, payload.byteOffset, CHUNK_VOLUME * 2) : payload;
  const out = new Uint8Array(CHUNK_HEADER + bytes.byteLength);
  const dv = new DataView(out.buffer);
  dv.setUint8(0, BIN_CHUNK);
  dv.setUint8(1, dimIndex(dim));
  dv.setUint16(2, raw ? 0 : CF_DEFLATE, true);
  dv.setInt32(4, cx, true);
  dv.setInt32(8, cz, true);
  out.set(bytes, CHUNK_HEADER);
  return out;
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Decode a chunk frame (browser and Node 20+: uses DecompressionStream for compressed frames). */
export async function decodeChunk(frame: Uint8Array): Promise<{ dim: DimensionId; cx: number; cz: number; blocks: Uint16Array } | null> {
  if (frame.byteLength < CHUNK_HEADER || frame[0] !== BIN_CHUNK) return null;
  const dv = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
  const dim = DIM_IDS[dv.getUint8(1)];
  if (!dim) return null;
  let body = frame.subarray(CHUNK_HEADER);
  if (dv.getUint16(2, true) & CF_DEFLATE) {
    try { body = await inflateRaw(body); } catch { return null; }
  }
  if (body.byteLength !== CHUNK_VOLUME * 2) return null;
  // copy: the frame may sit at an odd offset inside a larger buffer
  const blocks = new Uint16Array(CHUNK_VOLUME);
  new Uint8Array(blocks.buffer).set(body);
  return { dim, cx: dv.getInt32(4, true), cz: dv.getInt32(8, true), blocks };
}

// ---- mobs and world (server/actions/mobs.ts, world.ts, bosses.ts) ------------

export type ClientMsgMobs =
  /** Rider input while mounted, 20 Hz: forward / strafe -1..1, jump held, sneak held (sneak gets off). */
  | { t: 'steer'; f: number; s: number; j: boolean; sn: boolean }
  /** Buy offer `i` of the open trade window once; the price comes out of the inventory. */
  | { t: 'trade'; win: number; i: number }
  /** Left-click on an entity that is not a mob or player: a boat, an end crystal, the dragon. */
  | { t: 'strike'; id: number };

/** Boat record: [wood index (entities/vehicles/boat.ts BOAT_WOODS), x, y, z, yaw]. */
export type BoatRecord = [number, number, number, number, number];
/** End crystal record: [x, y, z, showBase]. Dragon record: [x, y, z, yaw, health, maxHealth]. */
export type CrystalRecord = [number, number, number, number];
export type DragonRecord = [number, number, number, number, number, number];

export type ServerMsgMobs =
  /** This client now sits on entity `id` (on) or left it; when leaving, (x, y, z) is where it stands. */
  | { t: 'mount'; id: number; on: boolean; x?: number; y?: number; z?: number }
  /** Offers of the open trade window (game/trading.ts OfferRow), the merchant's xp and level. Resent after every trade. */
  | { t: 'offers'; win: number; o: unknown[]; xp: number; level: number }
  /** Boss bar: fraction 0..1 of boss `id`; f < 0 removes the bar. */
  | { t: 'boss'; id: number; name: string; f: number }
  /** A lightning bolt struck at (x, y, z). */
  | { t: 'lightning'; x: number; y: number; z: number };
